import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { deleteImage, getImages, uploadMedia } from "../services/servicesblogs";
import "./AdminImages.css";

const getVideoMimeType = (src) => {
  if (/\.webm(?:$|\?)/i.test(src)) return "video/webm";
  if (/\.ogg(?:$|\?)/i.test(src)) return "video/ogg";
  return "video/mp4";
};

export default function AdminImages() {
  const fileInputRef = useRef(null);
  const [media, setMedia] = useState([]);

  const normalizeMedia = (item, index) => {
    const src =
      item?.url ??
      item?.fileUrl ??
      item?.secure_url ??
      item?.imageUrl ??
      item?.src ??
      "";

    return {
      id: item?.id || item?._id || `${Date.now()}-${index}`,
      name:
        item?.name ||
        item?.filename ||
        item?.originalname ||
        "uploaded-file",
      src,
      type:
        item?.type ||
        (/\.(mp4|webm|ogg)(?:$|\?)/i.test(src) ? "video" : "image"),
      uploadedPretty: item?.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
    };
  };

  const loadMedia = useCallback(async () => {
    try {
      const payload = await getImages();
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.images)
            ? payload.images
            : [];

      const normalized = list
        .map(normalizeMedia)
        .filter((item) => item.src !== "");

      setMedia(normalized);
    } catch (error) {
      console.error("Error loading media:", error);
      setMedia([]);
    }
  }, []);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const openFileExplorer = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      await Promise.all(files.map((file) => uploadMedia(file)));
      await loadMedia();
    } catch (error) {
      window.alert(error?.response?.data?.message || "Upload failed.");
    }

    event.target.value = "";
  };

  const handleDelete = async (id) => {
    try {
      await deleteImage(id);
      await loadMedia();
    } catch (error) {
      window.alert(error?.response?.data?.message || "Failed to delete file.");
    }
  };

  return (
    <div className="blog-page admin-images-page">
      <section className="blog-hero">
        <div>
          <h1>Media Manager</h1>
          <p>Upload images or videos separately for blog usage.</p>
        </div>
        <div className="hero-meta">{media.length} files uploaded</div>
      </section>

      <section className="blog-shell">
        <article className="blog-form-card">
          <div className="sa-top">
            {/* <div>
              <h1>Media Manager</h1>
              <p className="page-subtitle">{media.length} files uploaded</p>
            </div> */}
            <button
              type="button"
              className="primary-btn"
              onClick={openFileExplorer}
            >
              Upload Media
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,video/mp4,video/webm,video/ogg"
            multiple
            className="hidden-file-input"
            onChange={handleUpload}
          />

          {media.length === 0 ? (
            <section className="image-manager-box">
              <div className="image-empty-state">
                <div className="image-empty-icon">MEDIA</div>
                <h4>No media uploaded</h4>
                <p>Upload images or videos to use in your blog posts</p>
              </div>
            </section>
          ) : (
            <section className="image-grid">
              {media.map((item) => (
                <article key={item.id} className="image-card">
                  <div className="image-thumb-wrap">
                    {item.type === "image" ? (
                      <img src={item.src} alt={item.name} />
                    ) : (
                      <video controls preload="metadata">
                        <source
                          src={item.src}
                          type={getVideoMimeType(item.src)}
                        />
                      </video>
                    )}

                    <div className="image-hover-actions">
                      <button
                        type="button"
                        className="image-action-btn delete"
                        onClick={() => handleDelete(item.id)}
                        title="Delete"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="image-card-info">
                    <p className="image-name">{item.name}</p>
                    <div className="image-meta">
                      <span>Uploaded</span>
                      <span>{item.uploadedPretty}</span>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          )}
        </article>
      </section>
    </div>
  );
}
